import React from 'react';
import { Box, Typography } from '@mui/material';
import { AppointmentTypes } from '../../../types/appointment.types';

/**
 * Summary cards for the Past appointments tab.
 *
 * 5 cards: Total, Completed, No Check-In, Escalated, Compliance Rate
 * Compliance = ((completed + escalated) / (total - cancelled)) * 100
 */

const COLORS = {
    border: '#E8ECF0',
    grayMedium: '#8A94A6',
    textPrimary: '#1a1a2e',
    green: '#10B981',
    red: '#EF4444',
    amber: '#D97706',
};

interface Props {
    summary: AppointmentTypes.Summary | null;
}

export const PastSummaryCards: React.FC<Props> = ({ summary }) => {
    if (!summary) return null;

    const denominator = summary.total - summary.cancelled;
    const complianceRate = denominator > 0
        ? Math.round(((summary.completed + summary.escalated) / denominator) * 100)
        : 0;
    const complianceColor = complianceRate >= 80 ? COLORS.green : COLORS.red;

    const cards = [
        { title: 'Total', value: summary.total, color: COLORS.textPrimary },
        { title: 'Completed', value: summary.completed, color: COLORS.green },
        { title: 'No Check-In', value: summary.noCheckIn, color: COLORS.red },
        { title: 'Escalated', value: summary.escalated, color: COLORS.amber },
        {
            title: 'Compliance Rate',
            value: `${complianceRate}%`,
            subtitle: 'excl. cancelled',
            color: complianceColor,
        },
    ];

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: '12px',
                mb: 2,
            }}
        >
            {cards.map((card) => (
                <Box
                    key={card.title}
                    sx={{
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '8px',
                        padding: '14px 16px',
                        backgroundColor: '#fff',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: COLORS.grayMedium,
                            fontFamily: 'Poppins, sans-serif',
                            lineHeight: 1.4,
                        }}
                    >
                        {card.title}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '26px',
                            fontWeight: 700,
                            color: card.color,
                            fontFamily: 'Poppins, sans-serif',
                            lineHeight: 1.3,
                            mt: '4px',
                        }}
                    >
                        {card.value}
                    </Typography>
                    {(card as any).subtitle && (
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: COLORS.grayMedium,
                                fontFamily: 'Poppins, sans-serif',
                                mt: '2px',
                            }}
                        >
                            {(card as any).subtitle}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
};
